import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getFieldsConfig } from '../utils/propertyFieldsConfig';
import { getPrixLabel, getPrixValue, formatPrixDisplay, getVille, getSecteur, getPropertyTitle, getFieldValue } from '../utils/propertyDataMapper';
import { canViewConfidential } from '../utils/permissionsHelper';
import DynamicQuickBar from '../components/property/DynamicQuickBar';
import DynamicSections from '../components/property/DynamicSections';
import ContactMatchingWidget from '../components/property/ContactMatchingWidget';
import DiffusionManagerModal from '../components/property/DiffusionManagerModal';
import PropertyShareButton from '../components/property/PropertyShareButton';
import PropertyViewContent from '../components/property/PropertyViewContent';
import AffectClientModal from '../components/shared/AffectClientModal';
import TaskCreateModal from '../components/TaskCreateModal';
import LinkedTasksSection from '../components/LinkedTasksSection';
import EmailSendModal from '../components/EmailSendModal';
import { toast } from 'sonner';
import { getNegociateurName as nomNegociateur } from '../utils/negociateur';
import { messageArchivage, messageSuppression, messageServeur } from '../utils/retraitPortails';
import BandeauBienInactif from '../components/property/BandeauBienInactif';
import { adresseRetourListe } from '../utils/navigation';

/**
 * PropertyViewPage - Page de consultation d'un bien (READ-ONLY)
 * Version v2 : Design conforme à la maquette validée
 * Route: /properties/:id/view
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyViewPage = ({
  userProfile,
  agencySettings,
  onEdit,
  onBack,
  openEmailModal,
  openClientAffectation,
  openHistorique,
  openAjouterEvenement,
  openAjouterTache,
  generatePropertyPDF,
  printPropertyWeb,
  openFicheBienModal,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [negociateurs, setNegociateurs] = useState([]);
  const [trackingSummary, setTrackingSummary] = useState(null);
  const [activeSection, setActiveSection] = useState('descriptif');
  
  // État pour la galerie d'images
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState([]);
  
  // États pour les dropdowns du header (clic au lieu de hover pur)
  const [showPrintMenu, setShowPrintMenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // lot BY — « Supprimer » est réservé à l'admin (et à un bien déjà archivé).
  const estAdmin = React.useMemo(() => {
    try {
      const role = JSON.parse(localStorage.getItem('user') || '{}')?.role;
      return role === 'admin_agence' || role === 'super_admin';
    } catch { return false; }
  }, []);
  
  // État pour les menus dropdown
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showDiffuserMenu, setShowDiffuserMenu] = useState(false);
  const [showDiffusionModal, setShowDiffusionModal] = useState(false);
  const [showAffectModal, setShowAffectModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  
  // État pour la génération PDF
  const [generatingPDF, setGeneratingPDF] = useState(false);
  
  // État favoris (persisté en localStorage, identique à PropertiesListPage)
  const [isFavorite, setIsFavorite] = useState(false);
  useEffect(() => {
    try {
      const favs = JSON.parse(localStorage.getItem('favoriteProperties') || '[]');
      setIsFavorite(favs.includes(id));
    } catch { setIsFavorite(false); }
  }, [id]);

  const handleToggleFavorite = () => {
    try {
      const favs = JSON.parse(localStorage.getItem('favoriteProperties') || '[]');
      const newFavs = favs.includes(id) ? favs.filter(x => x !== id) : [...favs, id];
      localStorage.setItem('favoriteProperties', JSON.stringify(newFavs));
      setIsFavorite(!isFavorite);
      toast.success(isFavorite ? 'Retiré des favoris' : 'Ajouté aux favoris');
    } catch (e) {
      toast.error('Erreur favoris');
    }
    setShowMoreMenu(false);
  };

  const handleArchive = async () => {
    if (!window.confirm(messageArchivage(property?.diffusion))) {
      setShowMoreMenu(false);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/properties/${id}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bien archivé');
      setShowMoreMenu(false);
      navigate(adresseRetourListe());  // lot CE
    } catch (e) {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(messageSuppression(property?.diffusion))) {
      setShowMoreMenu(false);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/properties/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bien supprimé');
      setShowMoreMenu(false);
      navigate(adresseRetourListe());  // lot CE
    } catch (e) {
      toast.error(messageServeur(e, 'Erreur lors de la suppression'));
    }
  };
  
  // Référence au conteneur scrollable
  const scrollContainerRef = useRef(null);
  
  useEffect(() => {
    if (id) {
      loadProperty();
      loadNegociateurs();
      loadTrackingSummary();
    }
  }, [id]);

  const loadTrackingSummary = async () => {
    try {
      const resp = await axios.get(`${API}/properties/${id}/tracking-summary`);
      setTrackingSummary(resp.data);
    } catch (_) { /* non bloquant */ }
  };
  
  // Détecter la section visible lors du scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = document.querySelector('.overflow-auto');
      if (!scrollContainer) return;
      
      const sectionIds = ['descriptif', 'financier', 'composition', 'capacite', 'localisation', 'mandat', 'medias', 'confidentiel', 'historique'];
      const scrollTop = scrollContainer.scrollTop;
      
      // Parcourir les sections de bas en haut pour trouver celle qui est visible
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const sectionId = sectionIds[i];
        const element = document.getElementById(`section-${sectionId}`);
        if (element) {
          const elementTop = element.offsetTop;
          // Une section est active si son haut est au-dessus ou proche du point de scroll
          // On utilise un offset de 50 pour tenir compte du padding
          if (scrollTop + 50 >= elementTop) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };
    
    const scrollContainer = document.querySelector('.overflow-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      // Déclencher une fois au chargement
      handleScroll();
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [property]);

  // Gestion des touches clavier pour la galerie
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!galleryOpen) return;
      if (e.key === 'Escape') {
        closeGallery();
      }
      if (e.key === 'ArrowRight' && galleryImages.length > 1) {
        setGalleryIndex((prev) => (prev + 1) % galleryImages.length);
      }
      if (e.key === 'ArrowLeft' && galleryImages.length > 1) {
        setGalleryIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryOpen, galleryImages.length]);
  
  const loadProperty = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      if (response.data) {
        setProperty(response.data);
      } else {
        setError('Bien non trouvé');
      }
    } catch (err) {
      console.error('Erreur chargement bien:', err);
      setError('Erreur lors du chargement du bien');
    } finally {
      setLoading(false);
    }
  };
  
  const loadNegociateurs = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/negociateurs`, { headers });
      if (response.data) {
        setNegociateurs(response.data);
      }
    } catch (err) {
      console.error('Erreur chargement négociateurs:', err);
    }
  };
  
  // Ce motif était le bon : il vit maintenant dans utils/negociateur.js, partagé par les
  // quatre autres endroits qui lisaient ce champ à leur manière.
  const getNegociateurName = () => nomNegociateur(property, negociateurs);
  
  // Helper pour formater une adresse (peut être un objet ou une string)
  const formatAddress = (adresse) => {
    if (!adresse) return 'N/C';
    if (typeof adresse === 'string') return adresse;
    if (typeof adresse === 'object') {
      // Handle address object with rue, code_postal, ville, pays
      return adresse.rue || 'N/C';
    }
    return 'N/C';
  };
  
  // Helper pour formater le code postal et ville
  const formatPostalCity = (obj) => {
    if (!obj) return '';
    const cp = obj.code_postal || '';
    const ville = obj.ville || '';
    return [cp, ville].filter(Boolean).join(' ');
  };
  
  const getNegociateurDetails = () => {
    const negoId = property?.negociateur_id || property?.negociateur?.negociateur;
    if (!negoId) return null;
    
    return negociateurs.find(n => n.id === negoId) || null;
  };
  
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      // lot CE — on retrouve la liste avec ses filtres, sa page et son tri.
      navigate(adresseRetourListe());
    }
  };
  
  const handleEdit = () => {
    navigate(`/properties/${id}/edit`);
  };
  
  // lot B (B3 + B5) — Garde-fous AVANT la fiche CLIENT, jamais sur la fiche agent (document
  // de travail) : une description qui contredit les chiffres, ou une fiche sans description ni
  // photo, ne doivent pas partir chez un acquéreur sans que le négociateur l'ait su.
  const confirmerFicheClient = () => {
    const descriptions = property?.descriptif?.descriptions || {};
    const blocs = Object.values(descriptions)
      .flatMap((parLangue) => Object.values(parLangue || {}))
      .filter((b) => b && typeof b === 'object');
    const texte = blocs.map((b) => (b.content || '').replace(/<[^>]*>/g, '').trim()).join('');
    const majDonnees = property?.donnees_cles_maj_at;
    const obsolete = majDonnees && blocs.some((b) => {
      const genere = b.generated_at || b.last_ai_optimization;
      return genere && new Date(genere) < new Date(majDonnees);
    });
    if (obsolete && !window.confirm(
      "La description a été générée AVANT la dernière modification du prix, de la surface ou du "
      + "loyer : elle peut contredire les chiffres de la fiche.\n\nGénérer quand même la fiche client ?"
    )) return false;
    const photos = (property?.medias?.photos || []).length;
    if ((!texte || photos === 0) && !window.confirm(
      `Cette fiche sera envoyée sans ${!texte ? 'description' : ''}`
      + `${!texte && photos === 0 ? ' ni ' : ''}${photos === 0 ? 'photo' : ''}. Continuer ?`
    )) return false;
    return true;
  };

  // Fonction pour générer et télécharger le PDF
  const handleGeneratePDF = async (version) => {
    if (generatingPDF) return;
    if (version !== 'confidential' && !confirmerFicheClient()) return;
    
    setGeneratingPDF(true);
    try {
      const response = await axios.get(`${API}/properties/${id}/fiche-bien`, {
        params: { version },
        responseType: 'blob'
      });
      
      // Créer un lien pour télécharger le fichier
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      
      // Nom du fichier basé sur la référence et la version
      const ref = property?.reference || 'sans-reference';  // lot B — jamais l'UUID
      const versionLabel = version === 'confidential' ? 'agent' : 'client';
      link.setAttribute('download', `fiche_bien_${ref}_${versionLabel}.pdf`);
      
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      
      // Libérer l'URL
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur génération PDF:', err);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    } finally {
      setGeneratingPDF(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-slate-600 font-medium">Chargement du bien...</p>
        </div>
      </div>
    );
  }
  
  if (error || !property) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4 mx-auto">
            <i className="bi bi-exclamation-triangle text-3xl text-red-600"></i>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Erreur</h2>
          <p className="text-slate-600 mb-6">{error || 'Bien non trouvé'}</p>
          <button onClick={handleBack} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all">
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  // === CONFIGURATION DYNAMIQUE ===
  const config = getFieldsConfig(property);
  
  // Extraire les données via le mapper
  const propertyTitle = getPropertyTitle(property);
  const prixLabel = getPrixLabel(property); // "Prix de cession", "Net vendeur", etc.
  const prixValue = formatPrixDisplay(property); // "250 000 €"
  const ville = getVille(property);
  const secteur = getSecteur(property);
  
  // Gestion des droits
  const hasConfidentialAccess = canViewConfidential(userProfile, agencySettings);
  
  // Médias
  const medias = property.medias || {};
  const photos = medias.photos || [];
  const documents = medias.documents || [];
  const videoUrl = medias.video_url || null;
  const visiteVirtuelleUrl = medias.visite_virtuelle_url || null;
  
  // Image hero
  const heroImage = photos[0]?.url || null;
  const hasPhotos = photos.length > 0;

  // Fonctions pour la galerie
  const openGallery = (images, startIndex = 0) => {
    setGalleryImages(images);
    setGalleryIndex(startIndex);
    setGalleryOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeGallery = () => {
    setGalleryOpen(false);
    document.body.style.overflow = '';
  };

  const nextImage = () => {
    setGalleryIndex((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setGalleryIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  // Déterminer le type de bien pour affichage conditionnel
  const isCHR = property.profil === 'chr';
  const isServicesLiberaux = property.profil === 'services_liberaux';
  const operation = property.operation || property.taxonomy?.operation;
  const isCession = operation === 'cession';
  const isLocation = operation === 'location';
  const isVente = operation === 'vente_immo';

  // Navigation sections - dynamique selon type de bien
  const sections = [
    { 
      id: 'descriptif', 
      label: 'Fiche Technique', 
      icon: 'bi-file-text',
      hasMissingData: !property.descriptif?.descriptions?.['Fiche Technique']?.Français?.content && !property.descriptif?.descriptions?.Web?.Français?.content && !property.bien_general?.description_courte
    },
    { 
      id: 'financier', 
      label: 'Conditions Financières', 
      icon: 'bi-currency-euro',
      hasMissingData: !property.informations_vente?.prix_cession && !property.informations_vente?.prix_annonce && !property.informations_bail?.loyer_mensuel
    },
    { 
      id: 'composition', 
      label: 'Composition', 
      icon: 'bi-grid-3x3',
      hasMissingData: !property.composition?.superficie_totale && !property.composition?.surface_totale
    },
    // Section Capacité uniquement pour CHR
    ...(isCHR ? [{ 
      id: 'capacite', 
      label: 'Capacité', 
      icon: 'bi-people',
      hasMissingData: property.composition?.capacite === undefined || property.composition?.capacite === null
    }] : []),
    { 
      id: 'localisation', 
      label: 'Localisation', 
      icon: 'bi-geo-alt',
      hasMissingData: false
    },
    { 
      id: 'mandat', 
      label: 'Mandat & Négociateur', 
      icon: 'bi-file-earmark-text',
      hasMissingData: !property.numero_mandat
    },
    { 
      id: 'medias', 
      label: 'Médias', 
      icon: 'bi-camera',
      hasMissingData: photos.length === 0
    },
    { 
      id: 'confidentiel', 
      label: 'Confidentiel', 
      icon: 'bi-lock',
      hasMissingData: false
    },
    { 
      id: 'historique', 
      label: 'Historique', 
      icon: 'bi-clock-history',
      hasMissingData: false
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      
      {/* GALERIE MODAL */}
      {galleryOpen && galleryImages.length > 0 && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={closeGallery}>
          <button 
            onClick={closeGallery}
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition z-10"
          >
            <i className="bi bi-x-lg text-2xl"></i>
          </button>
          
          <div className="absolute top-4 left-4 bg-black/50 text-white px-4 py-2 rounded-full text-sm font-medium">
            {galleryIndex + 1} / {galleryImages.length}
          </div>
          
          {galleryImages.length > 1 && (
            <>
              <button 
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition"
              >
                <i className="bi bi-chevron-left text-3xl"></i>
              </button>
              
              <button 
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition"
              >
                <i className="bi bi-chevron-right text-3xl"></i>
              </button>
            </>
          )}
          
          <div className="max-w-[90vw] max-h-[85vh] flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <img 
              src={galleryImages[galleryIndex]?.url} 
              alt={galleryImages[galleryIndex]?.name || `Image ${galleryIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
          
          {galleryImages[galleryIndex]?.name && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm max-w-[80vw] truncate">
              {galleryImages[galleryIndex].name}
            </div>
          )}
        </div>
      )}
      
      {/* HEADER avec Navigation Pills - STYLE PropertyEditPage
          MOBILE (< md) : le titre est masqué ici — il reste affiché en gros sur le HERO
          plus bas, donc plus de doublon. Les actions passent en scroll horizontal
          discret pour ne jamais recouvrir/écraser le titre. */}
      <header className="bg-white shadow-sm sticky top-0 z-30">
        {/* lot BX — un bien supprimé ou archivé se reconnaît DÈS l'ouverture du lien. */}
        <BandeauBienInactif property={property} />
        {/* Ligne 1 : Titre et actions */}
        <div className="px-4 lg:px-8 py-3 md:py-4 flex items-center justify-between gap-2 border-b border-slate-100">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button 
              onClick={handleBack}
              className="text-slate-400 hover:text-slate-600 bg-slate-100 p-2.5 rounded-full transition shrink-0"
              data-testid="property-view-back-btn"
              aria-label="Retour"
            >
              <i className="bi bi-arrow-left"></i>
            </button>
            {/* Titre : caché en mobile (affiché en gros sur le Hero photo).
                Sur mobile on affiche uniquement la référence pour situer le bien. */}
            <div className="min-w-0 flex-1">
              <h1 className="hidden md:block text-xl font-bold text-slate-900 truncate">{propertyTitle}</h1>
              <p className="text-xs text-slate-500 md:mt-1 truncate">
                <i className="bi bi-geo-alt"></i> {ville} <span className="mx-1">•</span>
                <i className="bi bi-hash"></i> {property.reference || property.id?.slice(0, 8)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-1 md:gap-1.5 shrink-0">
            {/* Imprimer avec dropdown CLIQUABLE */}
            <div className="relative">
              <button 
                onClick={() => setShowPrintMenu(v => !v)}
                data-testid="property-view-print-btn"
                className="inline-flex items-center gap-1.5 h-9 px-3 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm font-medium text-slate-700 transition disabled:opacity-50"
                title="Imprimer"
                disabled={generatingPDF}
              >
                {/* lot AV6 — les icônes portent #007BFF PARTOUT : elles étaient grises ici et
                    bleues dans la barre du bas de la fiche. Le libellé reste gris, le contour
                    neutre : les deux barres restent deux barres (en haut le document, en bas
                    la relation). */}
                {generatingPDF ? (
                  <i className="bi bi-hourglass-split animate-spin text-[#007BFF]"></i>
                ) : (
                  <i className="bi bi-printer text-[#007BFF]"></i>
                )}
                <span className="hidden sm:inline">Imprimer</span>
                <i className="bi bi-chevron-down text-[10px]"></i>
              </button>
              {showPrintMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowPrintMenu(false)} />
                  <div className="absolute right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-lg shadow-xl z-50 min-w-[280px] py-1">
                    <div className="px-3 pb-2 mb-1 border-b border-slate-100 text-[10px] uppercase tracking-wider text-slate-500 font-bold">Choisir le format</div>
                    <button 
                      onClick={() => { setShowPrintMenu(false); handleGeneratePDF('confidential'); }}
                      disabled={generatingPDF}
                      data-testid="property-view-print-agent-btn"
                      className="w-full text-left px-3 py-2 hover:bg-rose-50 flex items-center gap-3 disabled:opacity-50 transition"
                    >
                      <div className="w-8 h-8 bg-rose-100 rounded-lg flex items-center justify-center">
                        <i className="bi bi-file-earmark-lock text-rose-600"></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-800">Fiche Agent</div>
                        <div className="text-[11px] text-slate-500">Avec confidentiel — 2 pages</div>
                      </div>
                    </button>
                    <button 
                      onClick={() => { setShowPrintMenu(false); handleGeneratePDF('public'); }}
                      disabled={generatingPDF}
                      data-testid="property-view-print-client-btn"
                      className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-center gap-3 disabled:opacity-50 transition"
                    >
                      <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <i className="bi bi-file-earmark-text text-emerald-600"></i>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-slate-800">Fiche Client</div>
                        <div className="text-[11px] text-slate-500">Sans confidentiel — 1 page</div>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
            
            {/* Signal de chaleur commerciale : consultations des fiches envoyées.
                Mobile : pastille compacte (icône flamme + compteur uniquement).
                Desktop : pastille avec texte complet. */}
            {trackingSummary && trackingSummary.viewers_count > 0 && (
              <div
                className="inline-flex items-center gap-1.5 h-9 px-2 md:px-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg text-sm font-semibold"
                data-testid="property-tracking-badge"
                title={`${trackingSummary.viewers_count} prospect(s) ont consulté la fiche · ${trackingSummary.total_views} vue(s)`}
              >
                <i className="bi bi-fire text-amber-500" />
                <span className="tabular-nums">{trackingSummary.viewers_count}</span>
                <span className="hidden md:inline">
                  prospect{trackingSummary.viewers_count > 1 ? 's' : ''}
                </span>
              </div>
            )}

            {/* Partager - avec génération du share_token (Sprint 1) */}
            <PropertyShareButton propertyId={property.id} propertyTitle={propertyTitle} />

            {/* Bouton « Simuler » retiré de l'en-tête (iter.72) :
                le simulateur est désormais **contextuel** dans la colonne
                « Actions rapides » de la fiche (piloté par l'opération). */}

            {/* Modifier — primaire */}
            <button 
              onClick={handleEdit}
              data-testid="property-view-edit-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#0062CC] hover:bg-[#0056b3] text-white rounded-lg text-sm font-bold shadow-sm transition"
            >
              <i className="bi bi-pencil"></i>
              <span className="hidden sm:inline">Modifier</span>
            </button>
            
            {/* Menu ⋯ CLIQUABLE */}
            <div className="relative">
              <button 
                onClick={() => setShowMoreMenu(v => !v)}
                data-testid="property-view-more-btn"
                className="w-9 h-9 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-600 flex items-center justify-center transition"
                title="Plus d'options"
              >
                <i className="bi bi-three-dots-vertical"></i>
              </button>
              {showMoreMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
                  <div className="absolute right-0 top-full mt-1.5 bg-white border border-slate-200 rounded-lg shadow-xl z-50 min-w-[220px] py-1">
                    <button onClick={handleToggleFavorite} data-testid="view-more-fav" className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-pink-50 flex items-center gap-2 transition">
                      <i className={`bi ${isFavorite ? 'bi-heart-fill text-red-500' : 'bi-heart text-pink-500'}`}></i>
                      {isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                    </button>
                    <div className="my-1 h-px bg-slate-100"></div>
                    <button 
                      onClick={handleArchive} data-testid="view-more-archive" className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-amber-50 flex items-center gap-2 transition">
                      <i className="bi bi-archive text-amber-500"></i>
                      Archiver
                    </button>
                    {/* lot BY — admin uniquement, et seulement un bien DÉJÀ archivé. */}
                    {estAdmin && property.status === 'archived' && (
                    <button onClick={handleDelete} data-testid="view-more-delete" className="w-full text-left px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2 transition">
                      <i className="bi bi-trash"></i>
                      Supprimer
                    </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ═════════ CONTENU v5 — HERO + KPIs + Sections + Sidebar (mockup property_view_preview_v5.html) ═════════ */}
      <PropertyViewContent
        property={property}
        negociateur={getNegociateurDetails()}
        agence={agencySettings}
        canViewConfidential={true}
        onShowConfidential={() => {/* TODO : modal de levée de confidentiel */}}
        onOpenDocument={(doc) => {
          if (doc?.url) {
            const url = doc.url.startsWith('http') ? doc.url : `${process.env.REACT_APP_BACKEND_URL || ''}${doc.url}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        }}
        onEmail={() => setShowEmailModal(true)}
        onPhone={() => {
          const nego = getNegociateurDetails();
          const propPhone = property?.informations_confidentielles?.proprietaire?.telephone;
          const target = nego?.telephone || nego?.phone || propPhone;
          if (target) {
            window.location.href = `tel:${target.replace(/\s/g, '')}`;
          } else {
            toast?.info ? toast.info('Aucun numéro disponible pour ce bien') : alert('Aucun numéro disponible pour ce bien');
          }
        }}
        onTask={() => setShowTaskModal(true)}
        onCalendar={() => {
          toast?.info
            ? toast.info('Module Agenda en cours de développement', { description: 'Disponible prochainement depuis le menu latéral.' })
            : alert('Module Agenda en cours de développement');
        }}
        onAssign={() => setShowAffectModal(true)}
        onBroadcast={() => setShowDiffusionModal(true)}
        onPropertyPatched={(patched) => setProperty(patched)}
      />

      {/* Section Tâches liées au bien */}
      {property && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-6">
          <LinkedTasksSection
            propertyId={property.id}
            defaultTaskTitle={property.titre_annonce ? `Suivi : ${property.titre_annonce}` : `Suivi bien ${property.reference || ''}`}
          />
        </div>
      )}

      {/* Modal Affectation à un client */}
      {showAffectModal && property && (
        <AffectClientModal
          isOpen={showAffectModal}
          onClose={() => setShowAffectModal(false)}
          propertyIds={[property.id]}
          onSuccess={(contact) => {
            setShowAffectModal(false);
            if (toast?.success) {
              toast.success(`Bien affecté à ${contact?.nom || contact?.prenom || 'ce contact'}`);
            }
          }}
        />
      )}

      {/* Modal de diffusion — UNIFIÉ (iter95) : portails + site vitrine + réseaux sociaux */}
      {showDiffusionModal && property && (
        <DiffusionManagerModal
          property={property}
          diffusion={property.diffusion}
          onClose={() => setShowDiffusionModal(false)}
          onChange={(newDiffusion) => setProperty(p => ({ ...p, diffusion: newDiffusion }))}
        />
      )}

      {/* Modal Nouvelle tâche liée au bien */}
      {showTaskModal && property && (
        <TaskCreateModal
          defaultLinkedPropertyId={property.id}
          defaultTitle={property.titre_annonce ? `Suivi : ${property.titre_annonce}` : ''}
          onClose={() => setShowTaskModal(false)}
          onCreated={() => setShowTaskModal(false)}
        />
      )}

      {/* Modal Envoi email */}
      {showEmailModal && property && (() => {
        const nego = getNegociateurDetails();
        const propEmail = property?.informations_confidentielles?.proprietaire?.email;
        const target = nego?.email || propEmail;
        return (
          <EmailSendModal
            defaultTo={target ? [target] : []}
            defaultSubject={`À propos du bien ${property.reference || ''}`}
            defaultBody={`Bonjour,\n\nJe vous contacte au sujet du bien ${property.reference || ''}${property.titre_annonce ? ` (${property.titre_annonce})` : ''}.\n\nCordialement.`}
            context="bien"
            onClose={() => setShowEmailModal(false)}
          />
        );
      })()}
    </div>
  );
};

export default PropertyViewPage;
